import { QueryClient, QueryKey } from "@tanstack/react-query";

import { persistentQueryClient } from "utils";

import {
  GET_CARBON_PROGRAM_OUTCOMES,
  GET_FARM_DATA_SUBMISSION_STATUS,
  GET_FIELDS,
  GET_FIELDS_CROP_RECOMMENDATIONS,
  GET_FIELD_DETAILS,
  GET_FIELD_PRACTICES,
  GET_VERIFICATION_EDITING_REQUESTS,
} from "consts";

import { FieldListField, FieldPracticeResponse, UpdatedCropYield } from "models";

export const queryIsSuccess = (query: { isSuccess: boolean }): boolean => query.isSuccess;

export const queryIsPending = (query: { isPending: boolean }): boolean => query.isPending;

export const queryIsFetching = (query: { isFetching: boolean }): boolean => query.isFetching;

export const queryIsLoading = (query: { isLoading: boolean }): boolean => query.isLoading;

export async function invalidateAndRefecthQueries(queryClient: QueryClient, queryKeys: QueryKey[]) {
  await Promise.all(queryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
  await Promise.all(queryKeys.map((queryKey) => queryClient.refetchQueries({ queryKey })));
}

export const invalidateCarbonProgramQueriesByChunk = (fieldId: string) => {
  // get the metadata for the GET_CARBON_PROGRAM_OUTCOMES query
  const queryClient = persistentQueryClient;
  const queryCache = queryClient.getQueryCache();
  const queryKeys = queryCache
    .getAll()
    .map((query) => query.queryKey)
    .filter(([firstKey]) => firstKey === GET_CARBON_PROGRAM_OUTCOMES);

  // find the query keys (format [GET_CARBON_PROGRAM_OUTCOMES, fieldId]) which relate to the chunk the field is in
  const queryKeyToInvalidate = queryKeys.filter(([_key, fieldIdsChunk]) => (fieldIdsChunk as string[]).includes(fieldId));

  queryKeyToInvalidate.forEach((key) => queryClient.invalidateQueries({ queryKey: key }));
};

export const invalidateQueriesByChunk = (queryKey: string, dependencyId: string) => {
  // get the metadata for the provided query key
  const queryClient = persistentQueryClient;
  const queryCache = queryClient.getQueryCache();
  const queryKeys = queryCache
    .getAll()
    .map((query) => query.queryKey)
    .filter(([firstKey]) => firstKey === queryKey);

  // find the query keys (format [queryKey, dependencyId]) which relate to the chunk the dependency is in
  const queryKeyToInvalidate = queryKeys.filter(([_key, fieldIdsChunk]) => (fieldIdsChunk as string | string[]).includes(dependencyId));

  queryKeyToInvalidate.forEach((key) => queryClient.invalidateQueries({ queryKey: key }));
};

// Everything that depends on a field's practice participations, e.g. its enrollment status
export const invalidateFieldParticipationQueries = (queryClient: QueryClient, fieldIds: string[], farmId?: string) => {
  fieldIds.forEach((fieldId) => {
    invalidateCarbonProgramQueriesByChunk(fieldId);
    invalidateQueriesByChunk(GET_FIELD_PRACTICES, fieldId);
  });
  queryClient.invalidateQueries({ queryKey: [GET_FIELDS] });
  queryClient.invalidateQueries({ queryKey: [GET_FARM_DATA_SUBMISSION_STATUS, farmId] });
  // Field details need to be re-fetched to reflect possible exceeding of hectare limit
  queryClient.invalidateQueries({ queryKey: [GET_FIELD_DETAILS] });
};

export const invalidateFieldPracticesByFarmId = (farmId: string) => {
  const queryClient = persistentQueryClient;
  const queryCache = queryClient.getQueryCache();
  const allQueries = queryCache.getAll();

  const farmFieldsList = allQueries.filter((query) => query.queryKey[0] === GET_FIELDS && query.queryKey[1] === farmId)?.[0]?.state.data as
    | FieldListField[]
    | undefined;
  if (!farmFieldsList) return;

  const fieldPracticesQueryKeysToInvalidate = allQueries
    .filter(
      (query) => query.queryKey[0] === GET_FIELD_PRACTICES && !!query.queryKey[1] && (typeof query.queryKey[1] === "string" || Array.isArray(query.queryKey[1]))
    )
    .filter((filteredQuery) => {
      if (typeof filteredQuery.queryKey[1] === "string") {
        return farmFieldsList.some((field) => field.id === filteredQuery.queryKey[1]);
      }
      return (filteredQuery.queryKey[1] as unknown as string[]).some((queryFieldId) => farmFieldsList.some((field) => field.id === queryFieldId));
    })
    .map((query) => query.queryKey);

  fieldPracticesQueryKeysToInvalidate.forEach((key) => queryClient.invalidateQueries({ queryKey: key }));
};

export const invalidateQueriesAfterFieldsBulkUpdate = (queryClient: QueryClient, fieldIds: string[]): Promise<void> => {
  // Collect unique query keys to invalidate to avoid invalidating the same chunk multiple times
  const invalidatedCarbonProgramQueryKeys = new Set<string>();
  const invalidatedFieldPracticesQueryKeys = new Set<string>();
  const invalidatedCropRecommendationsQueryKeys = new Set<string>();

  fieldIds.forEach((fieldId) => {
    const queryCache = queryClient.getQueryCache();
    const carbonProgramQueryKeys = queryCache
      .getAll()
      .map((query) => query.queryKey)
      .filter(([firstKey]) => firstKey === GET_CARBON_PROGRAM_OUTCOMES)
      .filter((queryKey) => {
        const [_key, fieldIdsChunk] = queryKey;
        return (fieldIdsChunk as string[]).includes(fieldId);
      })
      .map((key) => JSON.stringify(key));
    carbonProgramQueryKeys.forEach((key) => invalidatedCarbonProgramQueryKeys.add(key));

    const fieldPracticesQueryKeys = queryCache
      .getAll()
      .map((query) => query.queryKey)
      .filter(([firstKey]) => firstKey === GET_FIELD_PRACTICES)
      .filter(([_key, fieldIdsChunk]) => (fieldIdsChunk as string[]).includes(fieldId))
      .map((key) => JSON.stringify(key));
    fieldPracticesQueryKeys.forEach((key) => invalidatedFieldPracticesQueryKeys.add(key));

    invalidatedCropRecommendationsQueryKeys.add(JSON.stringify([GET_FIELDS_CROP_RECOMMENDATIONS, [fieldId]]));
  });

  // Invalidate each unique query key only once
  // Batch invalidations to prevent multiple refetches
  const allKeysToInvalidate = [
    ...Array.from(invalidatedCarbonProgramQueryKeys).map((keyStr) => JSON.parse(keyStr)),
    ...Array.from(invalidatedFieldPracticesQueryKeys).map((keyStr) => JSON.parse(keyStr)),
    ...Array.from(invalidatedCropRecommendationsQueryKeys).map((keyStr) => JSON.parse(keyStr)),
  ];

  return Promise.all([
    ...allKeysToInvalidate.map((key) => queryClient.invalidateQueries({ queryKey: key })),
    queryClient.invalidateQueries({ queryKey: [GET_VERIFICATION_EDITING_REQUESTS] }),
  ]).then(() => undefined);
};

export const applyUpdatedCropYieldsToCache = (queryClient: QueryClient, updatedCrops: UpdatedCropYield[]): void => {
  if (updatedCrops.length === 0) return;

  const updatedValuesByCropId = new Map(updatedCrops.map(({ farmFieldCropId, ...updatedValues }) => [farmFieldCropId, updatedValues]));
  const patchFieldPractices = (fieldPractices: FieldPracticeResponse): FieldPracticeResponse => {
    if (!Array.isArray(fieldPractices?.crops) || !fieldPractices.crops.some((crop) => updatedValuesByCropId.has(crop.id))) return fieldPractices;
    return { ...fieldPractices, crops: fieldPractices.crops.map((crop) => ({ ...crop, ...updatedValuesByCropId.get(crop.id) })) };
  };

  // bulk queries cache a list of fields, single-field queries a single one
  queryClient.setQueriesData<FieldPracticeResponse | FieldPracticeResponse[]>({ queryKey: [GET_FIELD_PRACTICES] }, (cachedPractices) =>
    Array.isArray(cachedPractices) ? cachedPractices.map(patchFieldPractices) : cachedPractices && patchFieldPractices(cachedPractices)
  );
};
